<template>
  <div class="reaction-picker" :class="{ 'reaction-picker--open': isOpen }">
    <!-- Trigger Button (current reaction or default) -->
    <button
      class="reaction-picker__trigger"
      :class="{
        'reaction-picker__trigger--active': currentReaction,
        'reaction-picker__trigger--delete-mode': showDeleteMode,
      }"
      @click.stop="handleTriggerClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @touchstart.passive="handleTouchStart"
      @touchend.passive="handleTouchEnd"
      @touchcancel.passive="handleTouchCancel"
    >
      <!-- Delete mode: shaking emoji with X button -->
      <span
        v-if="currentReaction && currentEmoji"
        class="reaction-picker__emoji"
        :class="{ 'reaction-picker__emoji--shaking': showDeleteMode }"
      >
        {{ currentEmoji }}
        <Transition name="delete-badge">
          <button
            v-if="showDeleteMode"
            class="reaction-picker__delete-btn"
            @click.stop="handleDeleteClick"
            aria-label="Remove reaction"
          >
            <v-icon size="10">mdi-close</v-icon>
          </button>
        </Transition>
      </span>
      <v-icon v-else size="18">mdi-heart-outline</v-icon>
      <span
        v-if="reactionCount"
        class="reaction-picker__count reaction-picker__count--clickable"
        @click.stop="emit('view-likers')"
      >
        {{ formatCount(reactionCount) }}
      </span>
    </button>

    <!-- Picker Popup -->
    <Teleport to="body">
      <Transition name="picker-pop">
        <v-theme-provider v-if="showPicker" :theme="uiStore.effectiveTheme">
          <div
            class="reaction-picker__popup"
          :style="popupStyle"
          role="dialog"
          aria-label="Choose a reaction"
          @click.stop
          @mouseenter="cancelClose"
          @mouseleave="scheduleClose"
          @keydown.escape="closePicker"
        >
          <!-- Reaction Grid View -->
          <div class="reaction-picker__grid">
            <button
              v-for="reaction in availableReactions"
              :key="reaction.name"
              class="reaction-picker__option"
              :class="{
                'reaction-picker__option--selected':
                  currentReaction === reaction.name,
              }"
              :title="reaction.description || reaction.name"
              :aria-label="`React with ${reaction.description || reaction.name}`"
              @click="selectReaction(reaction.name)"
            >
              <span class="reaction-picker__option-emoji">{{
                reaction.value
              }}</span>
            </button>
          </div>
          </div>
        </v-theme-provider>
      </Transition>
    </Teleport>

    <!-- Action Feedback Overlay -->
    <ActionFeedbackOverlay
      :show="feedback.show"
      :icon="feedback.icon"
      :text="feedback.text"
      :type="feedback.type"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, onUnmounted } from "vue";
import type { CSSProperties } from "vue";
import { useUiStore } from "@/stores/ui";
import { useSdk } from "@/composables/useSdk";
import { useActionFeedback } from "@/composables/useActionFeedback";
import ActionFeedbackOverlay from "@/components/feedback/ActionFeedbackOverlay.vue";
import type { UseAppSettingsReturn } from "@/composables/useAppSettings";

interface ReactionItem {
  name: string;
  value: string;
  description?: string;
}

interface Props {
  postUlid: string;
  currentReaction?: string | null;
  reactionCount?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "reaction-change": [reaction: string | null];
  "view-likers": [];
}>();

const { sdk } = useSdk();
const uiStore = useUiStore();
const appSettings = inject<UseAppSettingsReturn>("appSettings");
const { feedback, showFeedback } = useActionFeedback();

// State
const isOpen = ref(false);
const showPicker = ref(false);
const showDeleteMode = ref(false);
const triggerRect = ref<DOMRect | null>(null);
const closeTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const deleteModeTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const longPressTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const isProcessing = ref(false);

// Long press duration for mobile (ms)
const LONG_PRESS_DURATION = 500;
// Hover duration for desktop delete mode (ms)
const HOVER_DELETE_DELAY = 600;

// Default reactions (fallback when settings not loaded)
const defaultReactions: ReactionItem[] = [
  { name: "thumbs_up", value: "👍", description: "Like" },
  { name: "heart", value: "❤️", description: "Love" },
  { name: "fire", value: "🔥", description: "Fire" },
  { name: "clap", value: "👏", description: "Applause" },
  { name: "laugh", value: "😂", description: "Laugh" },
  { name: "sad", value: "😢", description: "Sad" },
  { name: "angry", value: "😠", description: "Angry" },
  { name: "mind_blown", value: "🤯", description: "Mind Blown" },
];

// Available reactions from settings
const availableReactions = computed((): ReactionItem[] => {
  if (appSettings?.reactions?.value && appSettings.reactions.value.length > 0) {
    // Map readonly array to mutable array with required fields
    return appSettings.reactions.value
      .filter((r) => r.name && r.value)
      .map((r) => ({
        name: r.name!,
        value: r.value!,
        description: r.description,
      }));
  }
  return defaultReactions;
});

// Get current reaction emoji
const currentEmoji = computed(() => {
  if (!props.currentReaction) return null;
  const reaction = availableReactions.value.find(
    (r) => r.name === props.currentReaction,
  );
  return reaction?.value || null;
});

// Popup positioning
const popupStyle = computed((): CSSProperties => {
  if (!triggerRect.value) return {};
  const rect = triggerRect.value;
  const popupWidth = 280;
  const popupHeight = 60;
  const padding = 8;

  // Calculate position (prefer above, fallback to below)
  let top = rect.top - popupHeight - padding;
  let left = rect.left + rect.width / 2 - popupWidth / 2;

  // Ensure within viewport
  if (top < padding) {
    top = rect.bottom + padding;
  }
  if (left < padding) {
    left = padding;
  }
  if (left + popupWidth > window.innerWidth - padding) {
    left = window.innerWidth - popupWidth - padding;
  }

  return {
    position: "fixed" as const,
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 9999,
  };
});

// Methods
function handleTriggerClick(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement;
  triggerRect.value = target.getBoundingClientRect();

  // If in delete mode, clicking the emoji itself should do nothing (X button handles delete)
  if (showDeleteMode.value) {
    return;
  }

  // If no reaction, open picker
  if (!props.currentReaction) {
    if (showPicker.value) {
      closePicker();
    } else {
      openPicker();
    }
  } else {
    // Has reaction - open picker to allow changing
    if (showPicker.value) {
      closePicker();
    } else {
      openPicker();
    }
  }
}

// Delete button click handler - optimistic update
function handleDeleteClick() {
  if (isProcessing.value || !props.currentReaction) return;

  const previousReaction = props.currentReaction;

  exitDeleteMode();

  // Optimistic update - show removal immediately
  emit("reaction-change", null);

  // Async API call - revert on failure (pass reaction name to SDK)
  sdk.removeReaction(props.postUlid, previousReaction).catch((err) => {
    console.error("Failed to remove reaction:", err);
    // Revert to previous state
    emit("reaction-change", previousReaction);
    showFeedback("mdi-alert-circle-outline", "Remove failed", "error");
  });
}

function openPicker() {
  isOpen.value = true;
  showPicker.value = true;
  exitDeleteMode();
  cancelClose();
}

function closePicker() {
  isOpen.value = false;
  showPicker.value = false;
}

function enterDeleteMode() {
  if (props.currentReaction && !showPicker.value) {
    showDeleteMode.value = true;
  }
}

function exitDeleteMode() {
  showDeleteMode.value = false;
  clearDeleteModeTimeout();
}

function clearDeleteModeTimeout() {
  if (deleteModeTimeout.value) {
    clearTimeout(deleteModeTimeout.value);
    deleteModeTimeout.value = null;
  }
}

// Desktop: hover to show delete mode (after delay), or open picker
function handleMouseEnter(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement;
  triggerRect.value = target.getBoundingClientRect();
  cancelClose();

  // If has reaction, show delete mode after delay
  if (props.currentReaction) {
    clearDeleteModeTimeout();
    deleteModeTimeout.value = setTimeout(() => {
      enterDeleteMode();
    }, HOVER_DELETE_DELAY);
  } else {
    // No reaction - show picker after delay
    closeTimeout.value = setTimeout(() => {
      openPicker();
    }, 300);
  }
}

function handleMouseLeave() {
  cancelClose();
  clearDeleteModeTimeout();

  // Exit delete mode when mouse leaves
  if (showDeleteMode.value) {
    // Small delay before exiting delete mode for better UX
    deleteModeTimeout.value = setTimeout(() => {
      exitDeleteMode();
    }, 200);
  }

  scheduleClose();
}

// Mobile: long press to show delete mode
function handleTouchStart() {
  if (props.currentReaction) {
    longPressTimeout.value = setTimeout(() => {
      enterDeleteMode();
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, LONG_PRESS_DURATION);
  }
}

function handleTouchEnd() {
  clearLongPressTimeout();
}

function handleTouchCancel() {
  clearLongPressTimeout();
}

function clearLongPressTimeout() {
  if (longPressTimeout.value) {
    clearTimeout(longPressTimeout.value);
    longPressTimeout.value = null;
  }
}

function scheduleClose() {
  cancelClose();
  closeTimeout.value = setTimeout(() => {
    closePicker();
  }, 400);
}

function cancelClose() {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value);
    closeTimeout.value = null;
  }
}

// Select reaction - optimistic update
function selectReaction(reactionName: string) {
  if (isProcessing.value) return;

  // Find the reaction object to get the emoji value
  const selectedReaction = availableReactions.value.find((r) => r.name === reactionName);
  if (!selectedReaction) {
    console.error("Reaction not found:", reactionName);
    return;
  }

  const previousReaction = props.currentReaction ?? null;

  closePicker();

  // If clicking on current reaction in picker, remove it
  if (reactionName === previousReaction) {
    // Optimistic update - show removal immediately
    emit("reaction-change", null);

    // Async API call - revert on failure (pass reaction name to SDK)
    if (previousReaction) {
      sdk.removeReaction(props.postUlid, previousReaction).catch((err) => {
        console.error("Failed to remove reaction:", err);
        emit("reaction-change", previousReaction);
        showFeedback("mdi-alert-circle-outline", "Remove failed", "error");
      });
    }
    return;
  }

  // Optimistic update - show new reaction immediately
  emit("reaction-change", reactionName);

  // Async API call - revert on failure
  const updateReaction = async () => {
    try {
      // Remove old reaction first if exists (pass reaction name to SDK)
      if (previousReaction) {
        await sdk.removeReaction(props.postUlid, previousReaction);
      }
      // Add new reaction (pass reaction name to SDK)
      await sdk.addReaction(props.postUlid, selectedReaction.name);
    } catch (err) {
      console.error("Failed to update reaction:", err);
      // Revert to previous state
      emit("reaction-change", previousReaction);
      showFeedback("mdi-alert-circle-outline", "Update failed", "error");
    }
  };

  updateReaction();
}

function formatCount(count: number): string {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + "M";
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + "K";
  }
  return count.toString();
}

// Close picker and exit delete mode on outside click
function handleOutsideClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (
    !target.closest(".reaction-picker") &&
    !target.closest(".reaction-picker__popup")
  ) {
    closePicker();
    exitDeleteMode();
  }
}

onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener("click", handleOutsideClick);
  cancelClose();
  clearDeleteModeTimeout();
  clearLongPressTimeout();
});
</script>

<style scoped lang="scss">
.reaction-picker {
  position: relative;
  display: inline-flex;

  &__trigger {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    border: none;
    background: none;
    color: rgba(var(--v-theme-on-surface), 0.6);
    font-size: 0.8125rem;
    cursor: pointer;
    border-radius: 9999px;
    transition: all 0.2s var(--ease-out-expo);

    &:hover {
      color: #f59e0b;
      background: rgba(245, 158, 11, 0.1);
    }

    &--active {
      color: #ef4444;

      &:hover {
        color: #dc2626;
        background: rgba(239, 68, 68, 0.1);
      }
    }

    &--delete-mode {
      background: rgba(239, 68, 68, 0.15);
    }
  }

  &__emoji {
    position: relative;
    font-size: 1.125rem;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &--shaking {
      animation: shake 0.4s ease-in-out infinite;
    }
  }

  &__delete-btn {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #dc2626;
    border: 2px solid rgb(var(--v-theme-surface));
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: all 0.15s ease;
    color: white;

    &:hover {
      background: #b91c1c;
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  &__count {
    font-weight: 500;

    &--clickable {
      cursor: pointer;
      transition: color 0.2s ease;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__popup {
    background: rgba(0, 0, 0, 0.4); // Match song card dark background
    border: 1px solid rgba(var(--v-theme-on-surface), 0.15); // Match song card border
    border-radius: 12px; // Match song card border radius
    padding: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(10px);
  }

  &__grid {
    display: flex;
    gap: 4px;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
      transform: scale(1.2);
    }

    &--selected {
      background: rgba(245, 158, 11, 0.2);
      border: 1px solid rgba(245, 158, 11, 0.4);

      &:hover {
        background: rgba(245, 158, 11, 0.3);
      }
    }
  }

  &__option-emoji {
    font-size: 1.25rem;
    line-height: 1;
  }
}

// Shake animation (Apple-style wiggle)
@keyframes shake {
  0%,
  100% {
    transform: rotate(-3deg);
  }
  50% {
    transform: rotate(3deg);
  }
}

// Delete badge transition
.delete-badge-enter-active {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.delete-badge-leave-active {
  transition: all 0.15s ease;
}

.delete-badge-enter-from {
  opacity: 0;
  transform: scale(0.5);
}

.delete-badge-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

// Picker popup transitions
.picker-pop-enter-active,
.picker-pop-leave-active {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.picker-pop-enter-from,
.picker-pop-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(8px);
}

// Light theme
.v-theme--light {
  .reaction-picker {
    &__popup {
      background: rgba(0, 0, 0, 0.05); // Lighter black for light theme
      border-color: rgba(var(--v-theme-on-surface), 0.15);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    }

    &__option {
      &:hover {
        background: rgba(var(--v-theme-on-surface), 0.05);
      }

      &--selected {
        background: rgba(217, 119, 6, 0.15);
        border-color: rgba(217, 119, 6, 0.3);

        &:hover {
          background: rgba(217, 119, 6, 0.25);
        }
      }
    }

    &__delete-btn {
      border-color: rgb(var(--v-theme-surface));
    }

    &__trigger {
      &--delete-mode {
        background: rgba(239, 68, 68, 0.1);
      }
    }
  }
}
</style>
